import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import Card from '../../../components/Card'
import { makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components'
import { useFormikContext } from 'formik'
import { FamilyLawModuleDto } from '../../../hooks/module/useFamilyLaw'
import { RecordTypeMap, RelationshipRecord, RelationshipType, RelationshipTypeMap } from './Relationship'
import { formatFullNumericDate } from '../../../helpers/dateHelper'
import { getFullName } from './Offspring/Offspring'
import { RequestType, RequestTypeMap } from './Requests/Requests'
import useCountries from '../../../hooks/useCountries'
import { useTranslation } from 'react-i18next'
import { getI18NName } from '../../Combobox/i18n/I18NCombobox'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        ...shorthands.gap(tokens.spacingHorizontalL, tokens.spacingVerticalS),
    },
    row: {
        minWidth: '0px',
        display: 'contents',
    },
})

const getAge = (dateOfBirth: string) => {
    const today = new Date()
    const birthDate = new Date(dateOfBirth)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    return monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? --age : age
}

export default function InfoCard(): ReactElement {
    const styles = useStyles()
    const { i18n } = useTranslation()

    const { values } = useFormikContext<FamilyLawModuleDto>()
    const { getCountry } = useCountries()
    const country = useMemo<string>(() => {
        return getI18NName(getCountry(values?.relationshipCountry), i18n.language)
    }, [values?.relationshipCountry])

    return (
        <Card>
            <Text size={500}>Relatieoverzicht</Text>
            <div className={mergeClasses(styles.container)}>
                <div className={mergeClasses(styles.row)}>
                    <Text>Relatievorm</Text>
                    <Text>{RelationshipTypeMap.get(values?.relationshipType)}</Text>
                </div>
                <div className={mergeClasses(styles.row)}>
                    <Text>Vastlegging</Text>
                    <Text>{RecordTypeMap.get(values?.relationshipRecord as RelationshipRecord)}</Text>
                </div>
                <div className={mergeClasses(styles.row)}>
                    <Text>Datum vastlegging</Text>
                    <Text>{formatFullNumericDate(values?.relationshipDate)}</Text>
                </div>
                {values?.relationshipType !== RelationshipType.Cohabitation && (
                    <>
                        <div className={mergeClasses(styles.row)}>
                            <Text>Plaats vastlegging</Text>
                            <Text>{values?.relationshipLocation}</Text>
                        </div>
                        <div className={mergeClasses(styles.row)}>
                            <Text>Land vastlegging</Text>
                            <Text>{country}</Text>
                        </div>
                    </>
                )}
            </div>
            <Text size={500}>Kinderen</Text>
            {values?.offspring && values?.offspring.length > 0 ? (
                <div>
                    <div className={mergeClasses(styles.container)}>
                        {values?.offspring.map((offspring, index) => {
                            return (
                                <div key={index} className={mergeClasses(styles.row)}>
                                    <Text>{getFullName(offspring.child.name)}</Text>
                                    <div>
                                        <Text>{formatFullNumericDate(offspring.child.dateOfBirth)} </Text>
                                        <Text size={200}>({getAge(offspring.child.dateOfBirth)} jaar)</Text>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            ) : (
                <Text italic size={200}>
                    Geen kinderen.
                </Text>
            )}
            <Text size={500}>Procedures</Text>
            {values?.requests && values?.requests.length > 0 ? (
                <div>
                    {values?.requests.map((request, index) => {
                        return (
                            <div key={index}>
                                <div className={mergeClasses(styles.container)}>
                                    <div className={mergeClasses(styles.row)}>
                                        <Text>{RequestTypeMap.get(request.type as RequestType)}</Text>
                                        {request.referenceDate && (
                                            <Text>Peildatum: {formatFullNumericDate(request.referenceDate)}</Text>
                                        )}
                                    </div>
                                </div>
                                <div className={mergeClasses(styles.container)}>
                                    <div className={mergeClasses(styles.row)}>
                                        <Text></Text>
                                        {request.decreeDate && (
                                            <Text>Beschikking: {formatFullNumericDate(request.decreeDate)}</Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <Text italic size={200}>
                    Geen verzoeken.
                </Text>
            )}
        </Card>
    )
}
